import { CHANNEL_DETAIL } from "@/apollo/queries/channels";
import ActiveLink from "@/components/ActiveLink";
import {
  ContractButtonLink,
  FavoriteButton,
  ShareButton,
} from "@/components/Buttons/index";
import Canvas from "@/components/Canvas";
import {
  ChannelSearchForm,
  ChannelSearchFormSp,
} from "@/components/Form/index";
import FormatPriceSVOD from "@/components/FormatPriceSVOD";
import ChannelHeroImage from "@/components/Images/ChannelHeroImage";
import Loading from "@/components/Loading";
import MemberFree from "@/components/MemberFree";
import ChannelDetailMetaTags, {
  tags,
} from "@/components/MetaTags/ChannelDetailMetaTags";
import OverflowMenu from "@/components/OverflowMenu";
import Palette from "@/components/Palette";
import { useBreadcrumbs, useChannelDetailQuery } from "@/hooks";
import { channelTag } from "@/lib/metaUtil";
import routes from "@/lib/routes";
import withMiddlewares from "@/middlewares/withMiddlewares";
import styles from "@/styles/Channel.module.scss";
import classnames from "classnames";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import IconContracted from "../../../svgs/icon_contracted.svg";
import IconTag from "../../../svgs/icon_tag.svg";
import { useCheckUrlHash } from "@/hooks/useCheckUrlHash";

export default function ChannelDetail() {
  useCheckUrlHash("program");
  const router = useRouter();
  const id = parseInt(router.query.id, 10);
  const hash =
    typeof window !== "undefined" ? window.location.hash.substring(1) : null;
  const [selectLink, setLink] = useState();
  const { setBreadcrumbs } = useBreadcrumbs();

  const { loading, data } = useChannelDetailQuery({
    variables: { id, withTabMenu: true, withEntitlement: true },
  });
  const channel = data?.channel;
  const entitlements = data?.entitlements;
  const course = data?.course;
  const isMemberFree =
    channel?.rights_control_type === "member_only" &&
    channel?.rights[0]?.price === 0;

  useEffect(() => {
    setBreadcrumbs([
      { text: "ホーム", href: routes.home },
      { text: channel.name },
    ]);
  }, [channel]);

  useEffect(() => {
    const tabMenu = channel?.tabMenu;
    if (tabMenu) {
      const link = tabMenu.find((tab) => tab.url === hash);
      if (link) {
        setLink(link);
      } else {
        setLink(tabMenu[0]);
        router.replace(
          { pathname: router.pathame, query: { id }, hash: tabMenu[0].url },
          undefined,
          { scroll: false, shallow: true }
        );
      }
    }
  }, [channel, hash]);

  const handleSearch = useCallback((data) => {
    router.push(routes.channelDetailSearch(id, data.search));
  }, []);

  return (
    <>
      {loading && <Loading fixed />}
      <ChannelDetailMetaTags
        channel={channel}
        link={selectLink}
        course={course}
      />
      <div className={styles.detail}>
        <ChannelHeroImage channel={channel} />
        <div className={styles.info}>
          <div className={styles.section}>
            <div className={styles.detail}>
              <h1 className={styles.title}>
                <span className={styles.titleText}>{channel.name}</span>
                {entitlements.length > 0 && (
                  <span className={styles.contractStatus}>
                    <IconContracted />
                    購入中
                  </span>
                )}
              </h1>
              <div className={styles.summary}>{channel.header}</div>
              {entitlements.length === 0 && (
                <div className={styles.price}>
                  {isMemberFree ? (
                    <MemberFree />
                  ) : (
                    <FormatPriceSVOD
                      price={channel?.rights?.[0]?.price}
                      course={course}
                    ></FormatPriceSVOD>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className={classnames(styles.section, styles.buttons)}>
            {entitlements.length == 0 && !isMemberFree && (
              <ContractButtonLink course={course} medium />
            )}
            <FavoriteButton metaId={channel.id} />
            <ShareButton title={channel.name} />
          </div>
        </div>
        <div className={styles.menu}>
          <div className={styles.tab}>
            {channel.tabMenu && (
              <OverflowMenu>
                {channel.tabMenu.map((tab) => {
                  if (tab.url === "program") return null;
                  else {
                    return (
                      <ActiveLink
                        key={`channel-${id}-${tab.url}`}
                        href={routes.channelDetail(id, tab.url)}
                        scroll={false}
                        shallow={false}
                        className={styles.link}
                      >
                        <span>{tab.title}</span>
                      </ActiveLink>
                    );
                  }
                })}
              </OverflowMenu>
            )}
          </div>
          <div className={styles.search}>
            <ChannelSearchForm onSubmit={handleSearch} />
            <ChannelSearchFormSp onSubmit={handleSearch} />
          </div>
        </div>
        <div className={styles.menuContent}>
          {channel.tabMenu && (
            <>
              {selectLink == channel.tabMenu[0] && (
                <div className={styles.verbose}>
                  <div className={styles.tag}>
                    <IconTag />
                    {channelTag(channel)}
                  </div>
                  <p>{channel.description}</p>
                </div>
              )}
              {selectLink?.values?.view_type == "canvas" && (
                <Canvas idKey={selectLink.values.canvas} hiddenPrice />
              )}
              {selectLink?.values?.view_type == "palette" && (
                <Palette id={selectLink.values.palette} hiddenPrice />
              )}
            </>
          )}
          {!channel.tabMenu && (
            <div className={styles.verbose}>
              <div className={styles.tag}>
                <IconTag />
                {channelTag(channel)}
              </div>
              <p>{channel.description}</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export const getServerSideProps = withMiddlewares(
  async ({ req, query }, { serverProps, error, apolloClient }) => {
    try {
      const id = parseInt(query.id, 10);
      if (isNaN(id)) {
        return { notFound: true };
      }
      const {
        data: { channel, course },
      } = await apolloClient.query({
        query: CHANNEL_DETAIL,
        variables: { id, withTabMenu: true, withEntitlement: true },
        fetchPolicy: "network-only",
      });

      if (channel) {
        const metatags = tags(channel, null, course);
        return {
          props: {
            ...serverProps,
            error,
            metatags,
            initialApolloState: apolloClient.cache.extract(),
          },
        };
      } else {
        return { notFound: true };
      }
    } catch (e) {
      const errorProperties = Object.getOwnPropertyNames(e).reduce(
        (obj, key) => {
          obj[key] = e[key];
          return obj;
        },
        {}
      );
      console.log(JSON.stringify(errorProperties));
      throw e;
    }
  }
);
